'use client';

import React, { useState, useMemo } from 'react';
import { Search, AlertTriangle, Package, Plus, Download, ChevronUp, ChevronDown, Edit2, Zap, Eye, X,  } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import Modal from '@/components/ui/Modal';
import { toast } from 'sonner';

// Mock data — backend: SELECT b.*, p.name as product_name, p.category FROM inventory_batches_fifo b JOIN unified_products_catalog p ON b.product_id = p.id WHERE b.supplier_id = auth.uid()
const today = new Date('2026-05-26');

function daysUntilExpiry(dateStr: string): number {
  const exp = new Date(dateStr);
  const diff = Math.floor((exp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  return diff;
}

const batches = [
  { id: 'batch-001', product_name: 'Fromage Frais Kiri 500g', product_category: 'Dairy', batch_ref: 'LOT-KF-0441', quantity_received: 120, quantity_remaining: 47, purchase_cost: 26.00, expiration_date: '2026-05-31', storage_condition: 'Chilled' as const, zone: 'Centre_Ville', supplier_ref: 'SUP-001' },
  { id: 'batch-002', product_name: 'Lait Entier Centrale 1L', product_category: 'Dairy', batch_ref: 'LOT-LC-0892', quantity_received: 240, quantity_remaining: 88, purchase_cost: 7.20, expiration_date: '2026-06-02', storage_condition: 'Chilled' as const, zone: 'Centre_Ville', supplier_ref: 'SUP-001' },
  { id: 'batch-003', product_name: 'Poulet Entier Halal 1.2kg', product_category: 'Meats', batch_ref: 'LOT-PH-0312', quantity_received: 80, quantity_remaining: 23, purchase_cost: 29.00, expiration_date: '2026-05-28', storage_condition: 'Frozen' as const, zone: 'Khwazat', supplier_ref: 'SUP-002' },
  { id: 'batch-004', product_name: 'Tomates Fraîches Calibre A', product_category: 'Vegetables', batch_ref: 'LOT-TF-1204', quantity_received: 500, quantity_remaining: 312, purchase_cost: 3.80, expiration_date: '2026-06-08', storage_condition: 'Dry' as const, zone: 'Ville_Haute', supplier_ref: 'SUP-003' },
  { id: 'batch-005', product_name: 'Yaourt Nature Danone 4×125g', product_category: 'Dairy', batch_ref: 'LOT-YD-0567', quantity_received: 200, quantity_remaining: 134, purchase_cost: 11.50, expiration_date: '2026-06-15', storage_condition: 'Chilled' as const, zone: 'Centre_Ville', supplier_ref: 'SUP-001' },
  { id: 'batch-006', product_name: 'Viande Hachée Bœuf 500g', product_category: 'Meats', batch_ref: 'LOT-VH-0788', quantity_received: 60, quantity_remaining: 12, purchase_cost: 35.00, expiration_date: '2026-05-27', storage_condition: 'Frozen' as const, zone: 'Khwazat', supplier_ref: 'SUP-002' },
  { id: 'batch-007', product_name: 'Pizza Margherita Surgelée', product_category: 'Pizzas', batch_ref: 'LOT-PM-0923', quantity_received: 150, quantity_remaining: 91, purchase_cost: 16.50, expiration_date: '2026-07-10', storage_condition: 'Frozen' as const, zone: 'Centre_Ville', supplier_ref: 'SUP-001' },
  { id: 'batch-008', product_name: 'Carottes Bio Botte 1kg', product_category: 'Vegetables', batch_ref: 'LOT-CB-1102', quantity_received: 300, quantity_remaining: 187, purchase_cost: 3.20, expiration_date: '2026-06-05', storage_condition: 'Dry' as const, zone: 'Ville_Haute', supplier_ref: 'SUP-003' },
  { id: 'batch-009', product_name: 'Beurre Paysan 250g', product_category: 'Dairy', batch_ref: 'LOT-BP-0334', quantity_received: 100, quantity_remaining: 65, purchase_cost: 13.00, expiration_date: '2026-08-20', storage_condition: 'Chilled' as const, zone: 'Centre_Ville', supplier_ref: 'SUP-001' },
  { id: 'batch-010', product_name: 'Escalope Dinde Halal 1kg', product_category: 'Meats', batch_ref: 'LOT-ED-0655', quantity_received: 40, quantity_remaining: 18, purchase_cost: 42.00, expiration_date: '2026-06-01', storage_condition: 'Frozen' as const, zone: 'Khwazat', supplier_ref: 'SUP-002' },
];

type SortField = 'expiration_date' | 'quantity_remaining' | 'product_name' | 'purchase_cost';
type SortDir = 'asc' | 'desc';

function getExpiryClass(days: number): string {
  if (days <= 7) return 'expiry-red';
  if (days <= 14) return 'expiry-orange';
  return '';
}

function getExpiryBadge(days: number) {
  if (days < 0) return { label: 'Expiré', class: 'bg-danger-light text-danger font-700' };
  if (days <= 7) return { label: `${days}j — CRITIQUE`, class: 'bg-danger-light text-danger font-700' };
  if (days <= 14) return { label: `${days}j — Alerte`, class: 'bg-warning-light text-warning font-700' };
  return { label: `${days}j`, class: 'bg-muted text-muted-foreground' };
}

export default function FIFOBatchContent() {
  const [search, setSearch] = useState('');
  const [storageFilter, setStorageFilter] = useState<'all' | 'Dry' | 'Chilled' | 'Frozen'>('all');
  const [expiryFilter, setExpiryFilter] = useState<'all' | 'critical' | 'warning' | 'normal'>('all');
  const [sortField, setSortField] = useState<SortField>('expiration_date');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selectedBatch, setSelectedBatch] = useState<typeof batches[0] | null>(null);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const processed = useMemo(() => {
    return batches
      .map((b) => ({ ...b, days_until_expiry: daysUntilExpiry(b.expiration_date) }))
      .filter((b) => {
        const matchSearch = b.product_name.toLowerCase().includes(search.toLowerCase()) ||
          b.batch_ref.toLowerCase().includes(search.toLowerCase());
        const matchStorage = storageFilter === 'all' || b.storage_condition === storageFilter;
        const matchExpiry =
          expiryFilter === 'all' ? true :
          expiryFilter === 'critical' ? b.days_until_expiry <= 7 :
          expiryFilter === 'warning' ? (b.days_until_expiry > 7 && b.days_until_expiry <= 14) :
          b.days_until_expiry > 14;
        return matchSearch && matchStorage && matchExpiry;
      })
      .sort((a, b) => {
        let aVal: number | string, bVal: number | string;
        if (sortField === 'expiration_date') { aVal = a.days_until_expiry; bVal = b.days_until_expiry; }
        else if (sortField === 'quantity_remaining') { aVal = a.quantity_remaining; bVal = b.quantity_remaining; }
        else if (sortField === 'purchase_cost') { aVal = a.purchase_cost; bVal = b.purchase_cost; }
        else { aVal = a.product_name; bVal = b.product_name; }
        if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
  }, [search, storageFilter, expiryFilter, sortField, sortDir]);

  const criticalCount = batches.filter((b) => daysUntilExpiry(b.expiration_date) <= 7).length;
  const warningCount = batches.filter((b) => { const d = daysUntilExpiry(b.expiration_date); return d > 7 && d <= 14; }).length;
  const totalValue = batches.reduce((sum, b) => sum + b.quantity_remaining * b.purchase_cost, 0);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronUp size={12} className="text-muted-foreground opacity-30" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="text-primary" /> : <ChevronDown size={12} className="text-primary" />;
  };

  return (
    <div className="space-y-5 fade-in">
      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-base metric-card-info">
          <p className="section-header mb-1">Total Lots</p>
          <p className="text-2xl font-800 text-foreground font-tabular">{batches.length}</p>
          <p className="text-xs text-muted-foreground">lots actifs en stock</p>
        </div>
        <div className="card-base metric-card-danger">
          <p className="section-header mb-1 text-danger">Critiques ≤7j</p>
          <p className="text-2xl font-800 text-danger font-tabular">{criticalCount}</p>
          <p className="text-xs text-muted-foreground">remises urgentes requises</p>
        </div>
        <div className="card-base metric-card-warning">
          <p className="section-header mb-1 text-warning">Alertes ≤14j</p>
          <p className="text-2xl font-800 text-warning font-tabular">{warningCount}</p>
          <p className="text-xs text-muted-foreground">planifier des promotions</p>
        </div>
        <div className="card-base metric-card-positive">
          <p className="section-header mb-1">Valeur Stock</p>
          <p className="text-2xl font-800 text-foreground font-tabular">
            {totalValue.toLocaleString('fr-MA', { maximumFractionDigits: 0 })}
          </p>
          <p className="text-xs text-muted-foreground">MAD — valeur d'achat restante</p>
        </div>
      </div>

      {/* Alert banner for critical */}
      {criticalCount > 0 && (
        <div className="flex items-start gap-3 p-4 bg-danger-light border border-danger/30 rounded-xl">
          <AlertTriangle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-700 text-danger">{criticalCount} lot(s) expirent dans ≤7 jours</p>
            <p className="text-xs text-muted-foreground mt-0.5">Activez des remises immédiates pour éviter les pertes. Les lots concernés sont surlignés en rouge ci-dessous.</p>
          </div>
          <button
            onClick={() => { setExpiryFilter('critical'); toast('Filtre critique appliqué'); }}
            className="btn-primary bg-danger hover:bg-red-700 text-xs py-2 px-3 flex-shrink-0"
          >
            <Zap size={13} />
            Voir Critiques
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="card-base p-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Rechercher lot ou produit..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-base pl-9"
            />
          </div>

          {/* Expiry filter */}
          <div className="flex gap-1.5">
            {(['all', 'critical', 'warning', 'normal'] as const).map((f) => (
              <button
                key={`expiry-${f}`}
                onClick={() => setExpiryFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-600 transition-all ${
                  expiryFilter === f
                    ? f === 'critical' ? 'bg-danger text-white' :
                      f === 'warning' ? 'bg-warning text-white' :
                      f === 'normal'? 'bg-success text-white' : 'bg-primary text-white' :'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {f === 'all' ? 'Tous' : f === 'critical' ? `🔴 Critique (${criticalCount})` : f === 'warning' ? `🟡 Alerte (${warningCount})` : '🟢 Normal'}
              </button>
            ))}
          </div>

          {/* Storage filter */}
          <div className="flex gap-1.5">
            {(['all', 'Dry', 'Chilled', 'Frozen'] as const).map((s) => (
              <button
                key={`storage-${s}`}
                onClick={() => setStorageFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-600 transition-all ${
                  storageFilter === s ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {s === 'all' ? 'Tous' : s === 'Dry' ? '☀️ Sec' : s === 'Chilled' ? '❄️ Réfrigéré' : '🧊 Congelé'}
              </button>
            ))}
          </div>

          <div className="ml-auto flex gap-2">
            <button className="btn-secondary text-xs py-1.5 px-3">
              <Download size={13} />
              Exporter
            </button>
            <button className="btn-primary text-xs py-1.5 px-3">
              <Plus size={13} />
              Nouveau Lot
            </button>
          </div>
        </div>
        {processed.length !== batches.length && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
            <span className="text-xs text-muted-foreground">{processed.length} lot(s) affiché(s)</span>
            <button
              onClick={() => { setSearch(''); setStorageFilter('all'); setExpiryFilter('all'); }}
              className="text-xs text-danger font-600 flex items-center gap-1 hover:underline"
            >
              <X size={11} /> Effacer filtres
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card-base p-0 overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Réf. Lot</th>
                <th
                  className="text-left px-4 py-3 text-xs font-600 text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('product_name')}
                >
                  <div className="flex items-center gap-1">Produit <SortIcon field="product_name" /></div>
                </th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Catégorie</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Stockage</th>
                <th
                  className="text-right px-4 py-3 text-xs font-600 text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('quantity_remaining')}
                >
                  <div className="flex items-center justify-end gap-1">Qté Restante <SortIcon field="quantity_remaining" /></div>
                </th>
                <th className="text-right px-4 py-3 text-xs font-600 text-muted-foreground">Qté Reçue</th>
                <th
                  className="text-right px-4 py-3 text-xs font-600 text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('purchase_cost')}
                >
                  <div className="flex items-center justify-end gap-1">Coût Achat <SortIcon field="purchase_cost" /></div>
                </th>
                <th className="text-right px-4 py-3 text-xs font-600 text-muted-foreground">Valeur Restante</th>
                <th
                  className="text-left px-4 py-3 text-xs font-600 text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('expiration_date')}
                >
                  <div className="flex items-center gap-1">Date Expiration <SortIcon field="expiration_date" /></div>
                </th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Jours Restants</th>
                <th className="text-right px-4 py-3 text-xs font-600 text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {processed.map((batch, idx) => {
                const expiryBadge = getExpiryBadge(batch.days_until_expiry);
                const rowHighlight = batch.days_until_expiry <= 7
                  ? 'bg-danger/5 hover:bg-danger/10'
                  : batch.days_until_expiry <= 14
                  ? 'bg-warning/5 hover:bg-warning/10'
                  : idx % 2 === 0 ? 'hover:bg-muted/40' : 'bg-muted/20 hover:bg-muted/40';
                const remainingValue = batch.quantity_remaining * batch.purchase_cost;

                return (
                  <tr key={batch.id} className={`transition-colors ${rowHighlight}`}>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">
                      {batch.batch_ref}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-600 text-foreground">{batch.product_name}</p>
                      <p className="text-2xs text-muted-foreground">{batch.zone.replace('_', ' ')}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded font-500">
                        {batch.product_category}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={batch.storage_condition} size="sm" />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-700 text-foreground font-tabular">{batch.quantity_remaining}</span>
                      <span className="text-2xs text-muted-foreground ml-1">/ {batch.quantity_received}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-muted-foreground font-tabular">
                      {batch.quantity_received}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-600 text-foreground font-tabular">
                      {batch.purchase_cost.toFixed(2)} MAD
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-700 text-foreground font-tabular">
                      {remainingValue.toLocaleString('fr-MA', { maximumFractionDigits: 0 })} MAD
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`text-xs font-600 px-2 py-1 rounded ${
                        batch.days_until_expiry <= 7
                          ? 'expiry-red'
                          : batch.days_until_expiry <= 14
                          ? 'expiry-orange' :'text-muted-foreground'
                      }`}>
                        {batch.expiration_date.split('-').reverse().join('/')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-600 ${expiryBadge.class}`}>
                        {expiryBadge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {batch.days_until_expiry <= 14 && (
                          <button
                            onClick={() => toast.success(`Remise activée sur ${batch.product_name}`)}
                            className="flex items-center gap-1 px-2 py-1 rounded-lg bg-accent-light text-accent text-2xs font-700 hover:bg-accent hover:text-white transition-colors"
                            title="Activer une remise rapide sur ce lot expirant"
                          >
                            <Zap size={11} />
                            Remise
                          </button>
                        )}
                        <button
                          onClick={() => setSelectedBatch(batch)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                          title="Voir les détails du lot"
                        >
                          <Eye size={13} />
                        </button>
                        <button
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                          title="Modifier ce lot"
                        >
                          <Edit2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {processed.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Package size={28} className="text-muted-foreground mb-3" />
            <p className="text-base font-700 text-foreground mb-1">Aucun lot trouvé</p>
            <p className="text-sm text-muted-foreground">Modifiez vos filtres ou ajoutez un nouveau lot de stock.</p>
          </div>
        )}
      </div>

      {/* Batch detail modal */}
      <Modal open={!!selectedBatch} onClose={() => setSelectedBatch(null)} title="Détails du Lot" size="md">
        {selectedBatch && (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Référence Lot', value: selectedBatch.batch_ref, mono: true },
                { label: 'Produit', value: selectedBatch.product_name },
                { label: 'Catégorie', value: selectedBatch.product_category },
                { label: 'Stockage', value: selectedBatch.storage_condition },
                { label: 'Qté Reçue', value: `${selectedBatch.quantity_received} unités` },
                { label: 'Qté Restante', value: `${selectedBatch.quantity_remaining} unités` },
                { label: 'Coût d\'Achat', value: `${selectedBatch.purchase_cost.toFixed(2)} MAD` },
                { label: 'Valeur Restante', value: `${(selectedBatch.quantity_remaining * selectedBatch.purchase_cost).toLocaleString('fr-MA', { maximumFractionDigits: 0 })} MAD` },
                { label: 'Date Expiration', value: selectedBatch.expiration_date.split('-').reverse().join('/') },
                { label: 'Jours Restants', value: `${selectedBatch.days_until_expiry} jours` },
              ].map((row) => (
                <div key={`detail-${row.label}`}>
                  <p className="text-2xs text-muted-foreground font-600 mb-0.5">{row.label}</p>
                  <p className={`text-sm font-700 text-foreground ${row.mono ? 'font-mono' : ''}`}>{row.value}</p>
                </div>
              ))}
            </div>
            {selectedBatch.days_until_expiry <= 14 && (
              <div className="p-3 bg-warning-light border border-warning/30 rounded-xl">
                <p className="text-xs font-700 text-warning flex items-center gap-1.5">
                  <AlertTriangle size={13} />
                  Ce lot expire bientôt — envisagez une remise de {selectedBatch.days_until_expiry <= 7 ? '15–20%' : '8–12%'} pour accélérer les ventes.
                </p>
              </div>
            )}
            <div className="flex gap-3 pt-2 border-t border-border">
              <button onClick={() => setSelectedBatch(null)} className="btn-secondary flex-1 justify-center">Fermer</button>
              {selectedBatch.days_until_expiry <= 14 && (
                <button
                  onClick={() => { toast.success(`Remise activée sur ${selectedBatch.product_name}`); setSelectedBatch(null); }}
                  className="btn-accent flex-1 justify-center"
                >
                  <Zap size={14} />
                  Activer Remise
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}